<template>
  <div :class="[wrapperClasses, wrapperClass]">
    <Checkbox
      unstyled
      class="checkbox"
      :class="checkboxClasses"
      v-model="localModelValue"
      :inputId="$attrs.id"
      :binary="binary"
      :value="value"
      v-bind="$attrs"
      @focus="onFocus"
      @blur="onBlur"
      inputClass="checkbox-input"
      ref="checkboxRef"
    />
    <div
      class="flex flex-col items-start text-button-text-primary-text checkbox-content"
      @click="onClick($event)"
    >
      <label :for="$attrs.id" class="cursor-pointer">
        <slot />
      </label>
      <span v-if="hint" :class="hintClasses">{{ hint }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, defineProps, defineEmits } from "vue";
import Checkbox from "primevue/checkbox";
import { equals } from "@primeuix/utils/object";

const props = defineProps({
  value: { type: [Boolean, String, Number], default: false },
  modelValue: { type: [Array, Boolean, String, Number], default: null },
  class: null,
  style: null,
  wrapperClass: null,
  wrapperStyle: null,
  binary: { type: Boolean, default: true },
  size: { type: String, default: "md" },
  hint: { type: String, default: null },
  type: { type: String, default: "primary" },
  intermediate: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  iconAlignment: { type: String, default: "left" },
});

const emit = defineEmits(["click", "update:modelValue", "change"]);

const focused = ref(false);
const checkboxRef = ref();

const localModelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit("update:modelValue", value);
  },
});

const checkboxClasses = computed(() => {
  const isChecked = props.binary
    ? !!localModelValue.value
    : Array.isArray(localModelValue.value) && localModelValue.value.includes(props.value);

  return {
    [`checkbox-${props.type}`]: isChecked,
    intermediate: props.intermediate && isChecked,
    disabled: props.disabled,
    "self-center": props.hint || ["top", "bottom"].includes(props.iconAlignment),
    [`checkbox-${props.size}`]: props.size,
  };
});

const hintClasses = computed(() => `checkbox-hint-${props.type}`);

const wrapperClasses = computed(() => [
  "flex",
  "gap-2",
  "items-center",
  {
    "flex-row-reverse": props.iconAlignment === "right",
    "flex-col": props.iconAlignment === "top",
    "flex-col-reverse": props.iconAlignment === "bottom",
    disabled: props.disabled,
  },
]);

const updateModelValue = () => {
  if (props.binary) {
    return !props.modelValue;
  }

  if (Array.isArray(props.modelValue)) {
    return props.modelValue.includes(props.value)
      ? props.modelValue.filter(val => equals(val, props.value))
      : [...props.modelValue, props.value];
  }

  return [props.value];
};

const onClick = (event: MouseEvent) => {
  if (props.disabled) return;

  checkboxRef.value?.input?.click?.();

  const newModelValue = updateModelValue();

  emit("click", event);
  emit("update:modelValue", newModelValue);
  emit("change", event, newModelValue);
};

const onFocus = () => {
  focused.value = true;
};

const onBlur = () => {
  focused.value = false;
};
</script>
